import { type ReactElement } from 'react';
import { r, s } from '@/theme/tokens';
import { Box, Collapse, Skeleton, TableCell, TableRow } from '@/ui/primitives';
import { NESTED_ROW_NAME_TEXT_SX } from './EntityDailyHistoryPanel.constants';
import {
  CHILD_NAME_INDENT,
  EXPAND_ICON_GUTTER_WIDTH,
  HistoryNameCell,
  HistoryStatusesGrid,
  type NonArraySx,
} from './EntityDailyHistoryPanelRowParts';
import type { EntityDailyHistoryRow } from './EntityDailyHistoryPanel.types';

type EntityDailyHistoryRowsProps = {
  rows: EntityDailyHistoryRow[];
  expandedRowIds: Set<string>;
  onToggleRowExpansion: (rowId: string) => void;
  rowHeight: number;
  blockSize: number;
  nameColumnWidth: string;
  rowNameTextSx: NonArraySx;
  activityLabel: 'executions' | 'builds' | 'jobs' | 'runs';
  linksDisabled: boolean;
};

const INDENT_STEP_PX = 16;
const BASE_CHILD_INDENT_PX = Number.parseInt(CHILD_NAME_INDENT, 10);
const historyRowCellClassName = 'EntityDailyHistoryRow-cell';
const historyPanelRowSurface = 'var(--surface-card)';
const historyRowBaseSx: NonArraySx = {};

const renderNestedHistoryRows = ({
  rows,
  expandedRowIds,
  onToggleRowExpansion,
  rowHeight,
  blockSize,
  nameColumnWidth,
  rowNameTextSx,
  activityLabel,
  linksDisabled,
  nestingLevel,
}: EntityDailyHistoryRowsProps & {
  nestingLevel: number;
}): ReactElement[] =>
  rows.flatMap((row) => {
    const isExpanded = expandedRowIds.has(row.id);
    const hasChildren =
      row.hasExpandableChildren ?? Boolean(row.childRows && row.childRows.length > 0);
    const childIndent =
      nestingLevel === 0
        ? CHILD_NAME_INDENT
        : `${String(BASE_CHILD_INDENT_PX + (nestingLevel - 1) * INDENT_STEP_PX)}px`;

    const rowElements: ReactElement[] = [
      <Box
        key={`${row.id}-nested-row`}
        sx={[
          historyRowBaseSx,
          {
            backgroundColor: historyPanelRowSurface,
            display: 'grid',
            gridTemplateColumns: `${nameColumnWidth} minmax(0, 1fr)`,
            alignItems: 'center',
            minHeight: `${rowHeight}px`,
          },
        ]}
      >
        <Box
          className={historyRowCellClassName}
          sx={{
            backgroundColor: historyPanelRowSurface,
            px: s('xl'),
            py: s('none'),
            overflow: 'hidden',
          }}
        >
          <HistoryNameCell
            row={row}
            rowNameTextSx={NESTED_ROW_NAME_TEXT_SX}
            childRowIndent={childIndent}
            nameColor="textSecondary"
            showExpand={hasChildren}
            isExpanded={isExpanded}
            onToggle={
              hasChildren
                ? () => {
                    onToggleRowExpansion(row.id);
                  }
                : undefined
            }
            linksDisabled={linksDisabled}
          />
        </Box>
        <Box
          className={historyRowCellClassName}
          sx={{
            backgroundColor: historyPanelRowSurface,
            px: s('xl'),
            py: s('none'),
            minWidth: 0,
            width: '100%',
          }}
        >
          <HistoryStatusesGrid
            rowId={row.id}
            dailyHistory={row.dailyHistory}
            blockSize={blockSize}
            activityLabel={activityLabel}
            linksDisabled={linksDisabled}
          />
        </Box>
      </Box>,
    ];

    if (!hasChildren) {
      return rowElements;
    }

    rowElements.push(
      <Collapse
        key={`${row.id}-nested-children`}
        in={isExpanded}
        timeout="auto"
        sx={{
          '& .MuiCollapse-wrapperInner': {
            overflow: 'hidden',
          },
        }}
      >
        <Box sx={{ backgroundColor: historyPanelRowSurface }}>
          {row.childrenLoading
            ? Array.from({ length: row.childrenLoadingRowCount ?? 2 }, (_unused, index) => (
                <Box
                  key={`${row.id}-loading-${String(index)}`}
                  sx={[
                    historyRowBaseSx,
                    {
                      backgroundColor: historyPanelRowSurface,
                      display: 'grid',
                      gridTemplateColumns: `${nameColumnWidth} minmax(0, 1fr)`,
                      alignItems: 'center',
                      minHeight: `${rowHeight}px`,
                    },
                  ]}
                >
                  <Box
                    className={historyRowCellClassName}
                    sx={{
                      backgroundColor: historyPanelRowSurface,
                      px: s('xl'),
                      py: s('none'),
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        minWidth: 0,
                        width: '100%',
                        pl: childIndent,
                      }}
                    >
                      <Skeleton
                        variant="rounded"
                        width={index % 2 === 0 ? '68%' : '61%'}
                        sx={{
                          height: '14px',
                          transform: 'none',
                          borderRadius: r('pill'),
                          flexShrink: 0,
                        }}
                      />
                      <Box
                        sx={{
                          width: EXPAND_ICON_GUTTER_WIDTH,
                          ml: s('sm'),
                          flexShrink: 0,
                        }}
                      />
                    </Box>
                  </Box>
                  <Box
                    className={historyRowCellClassName}
                    sx={{
                      backgroundColor: historyPanelRowSurface,
                      px: s('xl'),
                      py: s('none'),
                      minWidth: 0,
                      width: '100%',
                      display: 'grid',
                      gridTemplateColumns: `repeat(${String(row.dailyHistory.length)}, minmax(0, 1fr))`,
                      columnGap: s('md'),
                      alignItems: 'center',
                    }}
                  >
                    {row.dailyHistory.map((historyCell) => (
                      <Skeleton
                        key={`${row.id}-${historyCell.day.toISOString()}-loading-${String(index)}`}
                        variant="rounded"
                        sx={{
                          width: '100%',
                          minWidth: `${blockSize}px`,
                          height: '14px',
                          transform: 'none',
                          borderRadius: r('sm'),
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              ))
            : null}
          {renderNestedHistoryRows({
            rows: row.childRows ?? [],
            expandedRowIds,
            onToggleRowExpansion,
            rowHeight,
            blockSize,
            nameColumnWidth,
            rowNameTextSx,
            activityLabel,
            linksDisabled,
            nestingLevel: nestingLevel + 1,
          })}
        </Box>
      </Collapse>,
    );

    return rowElements;
  });

const renderHistoryRows = ({
  rows,
  expandedRowIds,
  onToggleRowExpansion,
  rowHeight,
  blockSize,
  nameColumnWidth,
  rowNameTextSx,
  activityLabel,
  linksDisabled,
  nestingLevel,
}: EntityDailyHistoryRowsProps & {
  nestingLevel: number;
}): ReactElement[] =>
  rows.flatMap((row) => {
    const isExpanded = expandedRowIds.has(row.id);
    const hasChildren =
      row.hasExpandableChildren ?? Boolean(row.childRows && row.childRows.length > 0);
    const childGridFirstColumnWidth = nameColumnWidth;
    const childIndent =
      nestingLevel === 0
        ? CHILD_NAME_INDENT
        : `${String(BASE_CHILD_INDENT_PX + (nestingLevel - 1) * INDENT_STEP_PX)}px`;
    const rowElements: ReactElement[] = [
      <TableRow
        key={`${row.id}-row`}
        sx={[
          historyRowBaseSx,
          {
            backgroundColor: historyPanelRowSurface,
            height: `${rowHeight}px`,
          },
        ]}
      >
        <TableCell
          className={historyRowCellClassName}
          sx={{
            backgroundColor: historyPanelRowSurface,
            width: nameColumnWidth,
            minWidth: nameColumnWidth,
            maxWidth: nameColumnWidth,
            px: s('xl'),
            py: s('none'),
            verticalAlign: 'middle',
            borderBottom: 'none',
          }}
        >
          <HistoryNameCell
            row={row}
            rowNameTextSx={nestingLevel > 0 ? NESTED_ROW_NAME_TEXT_SX : rowNameTextSx}
            childRowIndent={nestingLevel > 0 ? childIndent : undefined}
            nameColor={nestingLevel > 0 ? 'textSecondary' : 'inherit'}
            showExpand={hasChildren}
            isExpanded={isExpanded}
            onToggle={
              hasChildren
                ? () => {
                    onToggleRowExpansion(row.id);
                  }
                : undefined
            }
            linksDisabled={linksDisabled}
          />
        </TableCell>
        <TableCell
          className={historyRowCellClassName}
          sx={{
            backgroundColor: historyPanelRowSurface,
            px: s('xl'),
            py: s('none'),
            verticalAlign: 'middle',
            borderBottom: 'none',
            width: '100%',
          }}
        >
          <HistoryStatusesGrid
            rowId={row.id}
            dailyHistory={row.dailyHistory}
            blockSize={blockSize}
            activityLabel={activityLabel}
            linksDisabled={linksDisabled}
          />
        </TableCell>
      </TableRow>,
    ];

    if (!hasChildren) {
      return rowElements;
    }

    rowElements.push(
      <TableRow
        key={`${row.id}-children`}
        sx={{ backgroundColor: historyPanelRowSurface, '& td': { borderBottom: 'none' } }}
      >
        <TableCell
          colSpan={2}
          sx={{ backgroundColor: historyPanelRowSurface, p: s('none'), borderBottom: 'none' }}
        >
          <Collapse
            in={isExpanded}
            timeout="auto"
            sx={{
              '& .MuiCollapse-wrapperInner': {
                overflow: 'hidden',
              },
            }}
          >
            <Box>
              {row.childrenLoading
                ? Array.from({ length: row.childrenLoadingRowCount ?? 2 }, (_unused, index) => (
                    <Box
                      key={`${row.id}-loading-${String(index)}`}
                      sx={[
                        historyRowBaseSx,
                        {
                          display: 'grid',
                          gridTemplateColumns: `${childGridFirstColumnWidth} minmax(0, 1fr)`,
                          alignItems: 'center',
                          minHeight: `${rowHeight}px`,
                        },
                      ]}
                    >
                      <Box
                        className={historyRowCellClassName}
                        sx={{
                          px: s('xl'),
                          py: s('none'),
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            minWidth: 0,
                            width: '100%',
                            pl: childIndent,
                          }}
                        >
                          <Skeleton
                            variant="rounded"
                            width={index % 2 === 0 ? '68%' : '61%'}
                            sx={{
                              height: '14px',
                              transform: 'none',
                              borderRadius: r('pill'),
                              flexShrink: 0,
                            }}
                          />
                          <Box
                            sx={{
                              width: EXPAND_ICON_GUTTER_WIDTH,
                              ml: s('sm'),
                              flexShrink: 0,
                            }}
                          />
                        </Box>
                      </Box>
                      <Box
                        className={historyRowCellClassName}
                        sx={{
                          px: s('xl'),
                          py: s('none'),
                          minWidth: 0,
                          width: '100%',
                          display: 'grid',
                          gridTemplateColumns: `repeat(${String(row.dailyHistory.length)}, minmax(0, 1fr))`,
                          columnGap: s('md'),
                          alignItems: 'center',
                        }}
                      >
                        {row.dailyHistory.map((historyCell) => (
                          <Skeleton
                            key={`${row.id}-${historyCell.day.toISOString()}-loading-${String(index)}`}
                            variant="rounded"
                            sx={{
                              width: '100%',
                              minWidth: `${blockSize}px`,
                              height: '14px',
                              transform: 'none',
                              borderRadius: r('sm'),
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  ))
                : null}
              {renderNestedHistoryRows({
                rows: row.childRows ?? [],
                expandedRowIds,
                onToggleRowExpansion,
                rowHeight,
                blockSize,
                nameColumnWidth,
                rowNameTextSx,
                activityLabel,
                linksDisabled,
                nestingLevel: nestingLevel + 1,
              })}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>,
    );

    return rowElements;
  });

export const EntityDailyHistoryRows = ({
  rows,
  expandedRowIds,
  onToggleRowExpansion,
  rowHeight,
  blockSize,
  nameColumnWidth,
  rowNameTextSx,
  activityLabel,
  linksDisabled,
}: EntityDailyHistoryRowsProps): ReactElement => (
  <>
    {renderHistoryRows({
      rows,
      expandedRowIds,
      onToggleRowExpansion,
      rowHeight,
      blockSize,
      nameColumnWidth,
      rowNameTextSx,
      activityLabel,
      linksDisabled,
      nestingLevel: 0,
    })}
  </>
);
